import React, { useState } from 'react'
import { Upload, Button, message, Progress, Alert, Typography, Space } from 'antd'
import { InboxOutlined, FilePdfOutlined, DeleteOutlined } from '@ant-design/icons'
import { useStore } from '../../store/useStore'
import { extractData, extractNonFinancial } from '../../services/apiService'

const { Dragger } = Upload
const { Text } = Typography

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

function FileUploader() {
  const {
    startExtraction,
    setExtractionProgress,
    setExtractionResult,
    setExtractionError,
    isExtracting,
    extractionProgress,
    modelConfigs,
    displayUnit,
    addToHistory,
    selectedFile,
    fileInfo,
    setSelectedFile,
    clearSelectedFile,
    setNonFinancialInfo,
    setNonFinancialLoading
  } = useStore()

  const beforeUpload = (file) => {
    if (file.type !== 'application/pdf') {
      message.error('只能上传PDF文件！')
      return false
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error('文件大小不能超过50MB！')
      return false
    }
    return false // 阻止自动上传
  }

  const handleFileChange = (info) => {
    if (info.fileList.length > 0) {
      const file = info.fileList[0].originFileObj
      setSelectedFile(file, {
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2),
        type: file.type,
        lastModified: new Date(file.lastModified).toLocaleString()
      })
      window.Feedback && window.Feedback.trackEvent({ event: 'file_upload', metadata: { type: 'pdf', sizeMB: (file.size / 1024 / 1024).toFixed(1) } })
    } else {
      clearSelectedFile()
    }
  }

  const handleExtract = async () => {
    if (!selectedFile) {
      message.warning('请先上传PDF文件')
      return
    }

    const validModels = Object.entries(modelConfigs)
      .filter(([_, config]) => config.valid && config.apiKey)
      .map(([role, config]) => {
        const { valid, ...rest } = config
        return { role, ...rest }
      })

    if (validModels.length === 0) {
      message.warning('请至少配置并验证一个模型')
      return
    }

    startExtraction()

    try {
      window.Feedback && window.Feedback.trackEvent({ event: 'start_extraction', metadata: { modelCount: validModels.length } })
      // V2.13: extractData 返回 { data, debugLog, pdfResult }
      const { data: result, debugLog, pdfResult } = await extractData(selectedFile, validModels, displayUnit, (progress) => {
        setExtractionProgress(progress)
      })

      // V1.7: 传递 debugLog 到 store
      setExtractionResult(result, debugLog)

      // 添加到历史记录
      addToHistory({
        id: Date.now(),
        fileName: selectedFile.name,
        timestamp: new Date().toISOString(),
        companyName: result.companyInfo?.name || '未知公司',
        reportPeriod: result.companyInfo?.reportPeriod || '',
        success: true
      })

      message.success('财务数据提取完成！')
      window.Feedback && window.Feedback.trackEvent({ event: 'complete_extraction', metadata: { modelCount: validModels.length, hasNonFinancial: validModels.length >= 3 } })

      // V2.13: 三模型模式下异步加载非财务信息
      if (validModels.length >= 3 && pdfResult) {
        setNonFinancialLoading(true)
        message.info('正在加载非财务信息...', 2)

        extractNonFinancial(pdfResult, validModels).then(({ data: nfData }) => {
          if (nfData?.nonFinancialInfo) {
            setNonFinancialInfo(nfData.nonFinancialInfo)
            message.success('非财务信息加载完成！')
          }
          setNonFinancialLoading(false)
        }).catch(() => {
          setNonFinancialLoading(false)
        })
      }
    } catch (error) {
      setExtractionError(error.message)
      message.error(`提取失败: ${error.message}`)
    }
  }

  const handleRemove = () => {
    clearSelectedFile()
  }

  return (
    <div className="upload-area">
      <Dragger
        beforeUpload={beforeUpload}
        onChange={handleFileChange}
        fileList={selectedFile ? [{
          uid: '-1',
          name: selectedFile.name,
          status: 'done',
          originFileObj: selectedFile
        }] : []}
        onRemove={handleRemove}
        accept=".pdf"
        maxCount={1}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">点击或拖拽PDF文件到此区域</p>
        <p className="ant-upload-hint">
          支持财务年报PDF，最大50MB
        </p>
      </Dragger>

      {fileInfo && (
        <Alert
          style={{ marginTop: 16 }}
          type="info"
          icon={<FilePdfOutlined />}
          message={
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>{fileInfo.name}</Text>
              <Text type="secondary">
                大小: {fileInfo.size} MB | 最后修改: {fileInfo.lastModified}
              </Text>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={handleRemove}
              >
                移除文件
              </Button>
            </Space>
          }
        />
      )}

      {isExtracting && (
        <div style={{ marginTop: 16 }}>
          <Progress
            percent={extractionProgress}
            status="active"
            format={(percent) => `正在处理... ${percent}%`}
          />
        </div>
      )}

      <Button
        type="primary"
        size="large"
        block
        style={{ marginTop: 16 }}
        onClick={handleExtract}
        disabled={!selectedFile || isExtracting}
        loading={isExtracting}
      >
        {isExtracting ? '正在提取数据...' : '开始提取数据'}
      </Button>
    </div>
  )
}

export default FileUploader
